import React, { useState, useEffect, useRef } from 'react';
import { BiArrowBack, BiCheckCircle, BiInfoCircle, BiRefresh } from 'react-icons/bi';
import { BsSend, BsReply } from 'react-icons/bs';

import axios from '../../../axios';
import * as url from '../../../utils/url';
import { formatTimeAgo } from '../../../utils/stringUtils';
import { toastContainer } from '../../../utils/toast';

// ---------------------------------------------------------------------------
// Phone / mobile number helpers
// ---------------------------------------------------------------------------

/**
 * Extracts an E.164 mobile number from text if pattern @+<digits> is found.
 * Per API spec: backend also parses @<mobileNumber> from body — FE still sends
 * targetMobileNumber explicitly for safety.
 */
const extractMobileNumber = (text) => {
    var match = text.match(/@(\+?\d{7,15})/);
    if (!match) return null;
    var num = match[1];
    // Normalize to E.164
    if (num.charAt(0) === '+') {
        return num;
    }
    // If 11 digits starting with 1, just prepend +
    if (num.length === 11 && num.charAt(0) === '1') {
        return '+' + num;
    }
    // If 10 digits (no country code), prepend +1
    if (num.length === 10) {
        return '+1' + num;
    }
    // Fallback: prepend +
    return '+' + num;
};

/**
 * Returns the senderPhone of the last PARTICIPANT message in the feed.
 * Fallback when no @+phone is in the composer text and no replyContext is set.
 */
const getLastParticipantMobile = (msgs) => {
    for (var i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].senderType === 'PARTICIPANT' && msgs[i].senderPhone) {
            return msgs[i].senderPhone;
        }
    }
    return null;
};

/**
 * Resolves targetMobileNumber for the POST /reply payload.
 * Priority: (1) @+phone explicit in text → (2) replyContext phone → (3) last participant phone
 */
const resolveTargetMobile = (text, msgs, replyCtx) => {
    var fromText = extractMobileNumber(text);
    if (fromText) return fromText;
    if (replyCtx && replyCtx.senderPhone) return replyCtx.senderPhone;
    return getLastParticipantMobile(msgs);
};

/**
 * Best-effort phone map: scans ADMIN reply messages for @+phone targeting a userId.
 *
 * The GET /messages API does not return senderMobileNumber on participant messages.
 * TODO: request backend to add senderMobileNumber to the message object.
 *
 * Workaround: when an admin replied "@+13105551234 ..." with targetUserId "65aabc...",
 * we infer that userId "65aabc..." owns that phone number and cache it.
 */
var buildPhoneMap = function(msgs) {
    var map = {};
    msgs.forEach(function(m) {
        if (m.senderType === 'ADMIN' && m.targetUserId && m.body) {
            var phoneMatch = m.body.match(/@(\+\d{7,15})/);
            if (phoneMatch) {
                map[m.targetUserId] = phoneMatch[1];
            }
        }
    });
    return map;
};

/**
 * Applies the phone map to fill senderPhone on participant messages where possible.
 */
var applyPhoneMap = function(msgs) {
    var phoneMap = buildPhoneMap(msgs);
    if (Object.keys(phoneMap).length === 0) return msgs;
    return msgs.map(function(m) {
        if (m.senderType === 'PARTICIPANT' && !m.senderPhone && phoneMap[m.userId]) {
            return Object.assign({}, m, { senderPhone: phoneMap[m.userId] });
        }
        return m;
    });
};

/**
 * Returns initials from a display name string.
 */
const getInitials = (name) => {
    if (!name || !name.trim()) return '—';
    var parts = name.trim().split(' ').filter(function(n) { return n.length > 0; });
    if (parts.length === 0) return '—';
    return parts.map(function(n) { return n[0]; }).join('').toUpperCase().slice(0, 2);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HashtagDrilldown = ({ match, history, user }) => {
    var currentUserId = user && user.profile && user.profile._id;
    var orgPrefix = (user && user.profile && user.profile.organizationId && user.profile.organizationId.smsGroupPrefix) || '';
    if (orgPrefix.endsWith('-')) orgPrefix = orgPrefix.slice(0, -1);
    const hashtag = (match.params.hashtag || '').toUpperCase();
    const titleHashtag = hashtag.charAt(0).toUpperCase() + hashtag.slice(1).toLowerCase();
    // Avoid double-prefixing: if hashtag already starts with the org prefix, don't prepend again
    var alreadyHasPrefix = orgPrefix && hashtag.toLowerCase().startsWith(orgPrefix.toLowerCase() + '-');
    const displayHashtag = (orgPrefix && !alreadyHasPrefix) ? orgPrefix + '-' + titleHashtag.toLowerCase() : titleHashtag.toLowerCase();

    // groupId always comes from URL param — route is /:hashtag/:groupId (UserChatsPage)
    const groupId = match.params.groupId || null;

    // --- Message feed ---
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOlder, setLoadingOlder] = useState(false);
    const [hasOlder, setHasOlder] = useState(false);
    // Cursor format per API spec: "<createdAtMs>_<messageId>"
    const [nextCursor, setNextCursor] = useState(null);

    // --- Composer ---
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);

    // --- Reply context ---
    // Set when user clicks Reply on a participant message
    // Shape: { messageId, senderName, senderPhone, userId } | null
    const [replyContext, setReplyContext] = useState(null);

    // --- Error ---
    const [error, setError] = useState(null);

    // --- Refs ---
    const chatRef = useRef(null);
    const textareaRef = useRef(null);

    // -----------------------------------------------------------------------
    // Hide parent scrollbar while drilldown is mounted
    // -----------------------------------------------------------------------
    useEffect(function() {
        document.body.style.overflow = 'hidden';
        return function() { document.body.style.overflow = ''; };
    }, []);

    // -----------------------------------------------------------------------
    // Mount: fetch latest messages
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!groupId) {
            setLoading(false);
            setError('Group not found. Please go back and try again.');
            return;
        }
        fetchMessages();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // -----------------------------------------------------------------------
    // Format raw API messages into the shape this component uses
    // -----------------------------------------------------------------------
    const formatMessages = (rawMessages) => {
        return rawMessages.map(function(m) {
            // userId is now a populated object { _id, firstName, lastName, mobileNumber }
            // Guard for backward compat in case it is still a plain string
            var userObj = (m.userId && typeof m.userId === 'object') ? m.userId : null;
            var userIdStr = userObj ? userObj._id : (m.userId || null);

            var firstName = (userObj && userObj.firstName) ? userObj.firstName : '';
            var lastName = (userObj && userObj.lastName) ? userObj.lastName : '';
            var fullName = (firstName + ' ' + lastName).trim();
            var senderName = fullName
                ? fullName
                : (m.isRecipient ? 'Recipient' : 'Not Registered');

            var senderPhone = (!m.isRecipient && userObj) ? (userObj.mobileNumber || null) : null;

            return {
                id: m._id,
                userId: userIdStr,
                targetUserId: m.targetUserId || null,
                senderType: m.isRecipient ? 'ADMIN' : 'PARTICIPANT',
                senderName: senderName,
                senderPhone: senderPhone,
                body: m.body,
                createdAt: m.createdAt,
                // sentiment from textSentimentsData (present on inbound participant messages)
                sentiment: (m.textSentimentsData && m.textSentimentsData.sentiment) || null,
                source: m.source || null,
                replyToId: m.replyToId || null,
                replyToBodyPreview: m.replyToBodyPreview || null,
            };
        });
    };

    // -----------------------------------------------------------------------
    // Fetch messages — GET /textify-group-chat/:groupId/messages
    // cursor = null: initial load (last 20 messages)
    // cursor = "<createdAtMs>_<messageId>": load older 20 messages
    // -----------------------------------------------------------------------
    const fetchMessages = (cursor) => {
        if (!cursor) setLoading(true);
        else setLoadingOlder(true);

        var params = { limit: 20 };
        if (cursor) params.before = cursor;

        axios.get(url.textifyGetGroupMessages(groupId), { params: params })
            .then(function(res) {
                var rawMessages = (res.data && res.data.messages) || [];
                var resNextCursor = (res.data && res.data.nextCursor) || null;
                var resHasMore = (res.data && res.data.hasMore) || false;

                var formatted = formatMessages(rawMessages);

                // Sort ascending by createdAt so oldest is at top, newest at bottom (standard chat order)
                var sortAsc = function(msgs) {
                    return msgs.slice().sort(function(a, b) {
                        return new Date(a.createdAt) - new Date(b.createdAt);
                    });
                };

                if (!cursor) {
                    // Initial load — sort asc, apply phone map, auto-scroll to bottom
                    var withPhones = applyPhoneMap(sortAsc(formatted));
                    setMessages(withPhones);
                    setHasOlder(resHasMore);
                    setNextCursor(resNextCursor);
                    setTimeout(function() {
                        if (chatRef.current) {
                            chatRef.current.scrollTop = chatRef.current.scrollHeight;
                        }
                    }, 0);
                } else {
                    // Load older — prepend older messages before existing, then sort to keep order correct
                    var container = chatRef.current;
                    var prevScrollHeight = (container && container.scrollHeight) || 0;

                    setMessages(function(prev) {
                        var combined = sortAsc(formatted.concat(prev));
                        return applyPhoneMap(combined);
                    });
                    setHasOlder(resHasMore);
                    setNextCursor(resNextCursor);

                    setTimeout(function() {
                        if (container) {
                            container.scrollTop = container.scrollHeight - prevScrollHeight;
                        }
                    }, 0);
                }
            })
            .catch(function(err) {
                console.error('[HashtagDrilldown] Error fetching messages:', err);
                setError('Failed to load messages. Please try again.');
            })
            .finally(function() {
                setLoading(false);
                setLoadingOlder(false);
            });
    };

    // -----------------------------------------------------------------------
    // Scroll handler — trigger load older when scrolling near top
    // -----------------------------------------------------------------------
    const handleScroll = () => {
        if (!chatRef.current) return;
        if (chatRef.current.scrollTop < 50 && hasOlder && !loadingOlder && !loading) {
            fetchMessages(nextCursor);
        }
    };

    // -----------------------------------------------------------------------
    // Reply button — pre-fill composer and set replyContext
    // -----------------------------------------------------------------------
    const handleReply = (msg) => {
        setReplyContext({
            messageId: msg.id,
            senderName: msg.senderName,
            senderPhone: msg.senderPhone,
            userId: msg.userId,
            quotedBody: msg.body,
        });
        // Pre-fill with @Name if available, otherwise @+phone
        var nameIsReal = msg.senderName && msg.senderName !== 'Not Registered' && msg.senderName !== 'Participant';
        var replyTag = nameIsReal ? ('@' + msg.senderName + ' ') : (msg.senderPhone ? ('@' + msg.senderPhone + ' ') : '@');
        setReplyText(replyTag);

        setTimeout(function() {
            if (textareaRef.current) {
                textareaRef.current.focus();
                var len = textareaRef.current.value.length;
                textareaRef.current.setSelectionRange(len, len);
            }
        }, 0);
    };

    const dismissReplyContext = () => {
        setReplyContext(null);
    };

    // -----------------------------------------------------------------------
    // Send — POST /textify-group-chat/reply
    // Payload: { groupId, body, targetMobileNumber }
    // -----------------------------------------------------------------------
    const handleSend = () => {
        var trimmed = (replyText || '').trim();
        if (!trimmed || sending || !groupId) return;

        // Resolve targetPhoneNumber:
        // (1) @+phone in body text → (2) replyContext phone → (3) last participant phone
        var targetPhoneNumber = resolveTargetMobile(trimmed, messages, replyContext);
        if (!targetPhoneNumber) {
            toastContainer('No recipient found. Click Reply on a message or use @+phone in your text.', 'error');
            return;
        }

        setSending(true);
        setError(null);

        // Strip @+phone from body — participant knows the text is for them.
        // targetMobileNumber is sent as a separate field for routing.
        var cleanBody = trimmed.replace(/@\+\d{7,15}\s*/, '').trim();

        // Build optional replyTo fields from replyContext
        var replyToId = (replyContext && replyContext.messageId) || undefined;
        var replyToBodyPreview = undefined;
        if (replyContext && replyContext.quotedBody) {
            // Strip the hashtag group name (e.g. "#dyquest-safety ") from the preview
            var previewText = replyContext.quotedBody.replace(/#\S+\s*/, '').trim();
            replyToBodyPreview = previewText.length > 100 ? previewText.slice(0, 100).trim() : previewText;
        }

        // Resolve targetUserId: from replyContext or by matching phone to a participant in messages
        var targetUserId = (replyContext && replyContext.userId) || null;
        if (!targetUserId && targetPhoneNumber) {
            for (var i = 0; i < messages.length; i++) {
                if (messages[i].senderPhone && messages[i].senderPhone === targetPhoneNumber && messages[i].userId) {
                    targetUserId = messages[i].userId;
                    break;
                }
            }
        }
        var payload = {
            groupId: groupId,
            hashtagName: hashtag.toLowerCase(),
            body: cleanBody,
            targetMobileNumber: targetPhoneNumber,
            targetUserId: targetUserId,
            mode: 'direct',
        };
        if (replyToId) { payload.replyToId = replyToId; }
        if (replyToBodyPreview) { payload.replyToBodyPreview = replyToBodyPreview; }

        // Optimistic UI — append pending message immediately
        var tempId = 'local-' + Date.now();
        var newMsg = {
            id: tempId,
            userId: currentUserId || null,
            targetUserId: (replyContext && replyContext.userId) || null,
            senderType: 'ADMIN',
            senderName: 'You',
            senderPhone: null,
            body: cleanBody,
            createdAt: new Date().toISOString(),
            sentiment: null,
            source: 'platform_reply',
            pending: true,
            replyToId: replyToId || null,
            replyToBodyPreview: replyToBodyPreview || null,
        };

        setMessages(function(prev) { return prev.concat(newMsg); });
        setReplyText('');
        setReplyContext(null);

        // Scroll to bottom after optimistic insert
        setTimeout(function() {
            if (chatRef.current) {
                chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
        }, 0);

        axios.post(url.textifySendMessage, payload)
            .then(function(res) {
                if (res.data && res.data.ok) {
                    // Replace temp message with real messageId from response
                    var realId = res.data.messageId;
                    setMessages(function(prev) {
                        return prev.map(function(m) {
                            return m.id === tempId ? Object.assign({}, m, { id: realId, pending: false }) : m;
                        });
                    });
                    toastContainer('Message sent.', 'success');
                } else {
                    throw new Error('Unexpected response from server');
                }
            })
            .catch(function(err) {
                console.error('[HashtagDrilldown] Error sending message:', err);
                // Remove optimistic message on failure
                setMessages(function(prev) { return prev.filter(function(m) { return m.id !== tempId; }); });
                var errData = err.response && err.response.data;
                var beMsg = errData && (errData.message || errData.error);
                var errMsg = (beMsg && beMsg.includes('you entered is not in')) ? beMsg : 'Failed to send message. Please try again.';
                toastContainer(errMsg, 'error');
            })
            .finally(function() {
                setSending(false);
            });
    };

    // -----------------------------------------------------------------------
    // Keyboard: Cmd/Ctrl + Enter to send
    // -----------------------------------------------------------------------
    const handleKeyDown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    // -----------------------------------------------------------------------
    // Name maps for admin message display: show @Name instead of @+phone
    // -----------------------------------------------------------------------
    var phoneNameMap = {};
    var userIdNameMap = {};
    messages.forEach(function(m) {
        if (m.senderType === 'PARTICIPANT') {
            if (m.senderPhone && m.senderName && m.senderName !== 'Participant') {
                phoneNameMap[m.senderPhone] = m.senderName;
            }
            if (m.userId && m.senderName && m.senderName !== 'Participant') {
                userIdNameMap[m.userId] = m.senderName;
            }
        }
    });

    // Fallback for unregistered participants with no name
    var displayName = function(name) {
        if (!name || name.trim() === '' || name.trim().toLowerCase() === 'undefined undefined' || name.trim().toLowerCase() === 'undefined') {
            return 'Not Registered';
        }
        return name;
    };

    // Strip @+phone from body for display (old messages have it, new ones don't)
    var cleanAdminBody = function(body) {
        return body.replace(/@\+\d{7,15}\s*/, '').trim();
    };

    // Find the most recent message from targetUserId before the given index
    var findQuotedMessage = function(targetUserId, msgIndex) {
        if (!targetUserId) return null;
        for (var i = msgIndex - 1; i >= 0; i--) {
            if (messages[i].userId === targetUserId) {
                return messages[i];
            }
        }
        return null;
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <div className="uc-drilldown-page">

            {/* Header — single row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
                <span onClick={function() { var overviewPath = match.url.replace(/\/[^/]+\/[^/]+$/, ''); history.push(overviewPath); }} className="cursor-pointer back-link" style={{ display: 'flex', alignItems: 'center', color: '#64748b', fontSize: '14px' }}>
                    <BiArrowBack className="me-1" /> User Chats
                </span>
                <span style={{ color: '#64748b', fontSize: '12px' }}>&gt;</span>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#0f172a' }}>#{displayHashtag}</h2>
                <span
                    onClick={function() { setMessages([]); setNextCursor(null); fetchMessages(); }}
                    style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#64748b' }}
                >
                    <BiRefresh size={16} /> Refresh chat
                </span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                    {loading ? 'Loading...' : (messages.filter(function(m) { return !m.pending; }).length + ' messages')}
                </span>
            </div>
            <div className="uc-info-badge" style={{ marginBottom: '12px' }}>
                <BiInfoCircle /> Recipients receive email notification only on the first use of a hashtag by a participant. Subsequent replies do not trigger additional notifications.
            </div>

            {/* Flat feed container */}
            <div className="uc-flat-feed-container">

                {/* Message thread */}
                <div
                    className="uc-message-thread"
                    ref={chatRef}
                    onScroll={handleScroll}
                >
                    {loadingOlder && (
                        <div className="loading-older">Loading older messages...</div>
                    )}

                    {!hasOlder && !loading && messages.length > 0 && (
                        <div className="beginning-of-conversation">
                            <BiCheckCircle className="me-1" /> Beginning of conversation
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-older" style={{ padding: '40px 0' }}>
                            Loading messages...
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="loading-older" style={{ padding: '40px 0' }}>
                            No messages yet in #{titleHashtag}
                        </div>
                    ) : (
                        messages.map(function(msg, msgIndex) {
                            var isMe = msg.userId === currentUserId;
                            var quotedMsg = msg.senderType === 'ADMIN' ? findQuotedMessage(msg.targetUserId, msgIndex) : null;
                            var quotePreview = msg.replyToBodyPreview || (quotedMsg ? quotedMsg.body : null);
                            var quoteName = quotedMsg ? displayName(quotedMsg.senderName) : (msg.targetUserId && userIdNameMap[msg.targetUserId] ? userIdNameMap[msg.targetUserId] : null);
                            var displayBody = msg.senderType === 'ADMIN' ? cleanAdminBody(msg.body) : msg.body;
                            return (
                                <div
                                    key={msg.id}
                                    className={'uc-msg ' + (isMe ? 'admin-msg' : 'user-msg')}
                                >
                                    {/* Message meta */}
                                    <div className="msg-meta">
                                        {!isMe && (
                                            <div
                                                className="uc-avatar mini"
                                                style={{ backgroundColor: '#e2e8f0', color: '#475569' }}
                                            >
                                                {getInitials(displayName(msg.senderName))}
                                            </div>
                                        )}
                                        <span className="uc-fw-500-dark">{displayName(msg.senderName)}</span>
                                        <span>{formatTimeAgo(msg.createdAt)}</span>
                                        {msg.senderPhone && (
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                {msg.senderPhone}
                                            </span>
                                        )}
                                        {msg.sentiment && (
                                            <span className={'uc-pill sentiment-' + msg.sentiment.toLowerCase() + ' mini'}>
                                                <span className="dot" style={{ fontSize: '8px' }}>●</span>
                                                {msg.sentiment.charAt(0) + msg.sentiment.slice(1).toLowerCase()}
                                            </span>
                                        )}
                                        {isMe && (
                                            <div className="uc-avatar mini">
                                                {getInitials(displayName(msg.senderName))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Message bubble */}
                                    <div className="msg-bubble">
                                        {quotePreview && (
                                            <div className="msg-quote">
                                                {quoteName && <span className="msg-quote-name">{quoteName}</span>}
                                                <span className="msg-quote-body">{quotePreview}</span>
                                            </div>
                                        )}
                                        {displayBody}
                                    </div>

                                    {/* Reply button — participant messages only */}
                                    {msg.senderType === 'PARTICIPANT' && !msg.pending && (
                                        <button
                                            className="uc-reply-btn"
                                            onClick={() => handleReply(msg)}
                                        >
                                            <BsReply style={{ marginRight: '4px' }} /> Reply
                                        </button>
                                    )}

                                    {/* Pending indicator */}
                                    {msg.pending && (
                                        <span className="uc-msg-pending">Sending...</span>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Composer */}
                <div className="uc-reply-composer">

                    {/* Reply context banner */}
                    {replyContext && (
                        <div className="uc-reply-context-banner">
                            <div className="uc-reply-context-info">
                                <span className="uc-reply-context-label">Replying to</span>
                                <span className="uc-reply-context-name">{displayName(replyContext.senderName)}</span>
                                {replyContext.senderPhone && (
                                    <span className="uc-reply-context-phone">{replyContext.senderPhone}</span>
                                )}
                            </div>
                            <button className="uc-reply-context-dismiss" onClick={dismissReplyContext}>
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Error banner */}
                    {error && (
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            borderRadius: '6px',
                            fontSize: '12px',
                            marginBottom: '8px',
                        }}>
                            {error}
                        </div>
                    )}

                    <textarea
                        ref={textareaRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message... Use @+phone to target a specific participant"
                        disabled={sending}
                    />

                    <div className="uc-composer-footer">
                        <span>Cmd+Enter to send &middot; #{titleHashtag.toLowerCase()}</span>
                        <button
                            className={'uc-send-btn ' + (!replyText.trim() || sending ? 'disabled' : '')}
                            disabled={!replyText.trim() || sending}
                            onClick={handleSend}
                        >
                            <BsSend /> {sending ? 'Sending...' : 'Send Reply'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HashtagDrilldown;
